import type { ShipmentImportRow } from "~/types/admin-shipping";

const requiredColumns = [
  "target_kind",
  "target_id",
  "tracking_number",
] as const;

interface CsvRecord {
  cells: string[];
  rowNumber: number;
}

export interface ParsedShipmentCsv {
  rows: ShipmentImportRow[];
  rowCount: number;
}

export class ShipmentCsvError extends Error {
  readonly rowNumber: number | undefined;

  constructor(message: string, rowNumber?: number) {
    super(message);
    this.name = "ShipmentCsvError";
    this.rowNumber = rowNumber;
  }
}

export function parseShipmentCsv(source: string): ParsedShipmentCsv {
  const records = parseCsvRecords(source.replace(/^\uFEFF/, ""));
  if (records.length === 0) {
    throw new ShipmentCsvError("CSV が空です。");
  }

  const header = records[0].cells.map((cell) => cell.trim());
  const duplicateColumns = header.filter(
    (column, index) => column.length > 0 && header.indexOf(column) !== index,
  );
  if (duplicateColumns.length > 0) {
    throw new ShipmentCsvError(
      `同じ列名が複数あります: ${[...new Set(duplicateColumns)].join(", ")}`,
      1,
    );
  }

  const columnIndexes = new Map(header.map((column, index) => [column, index]));
  const missingColumns = requiredColumns.filter(
    (column) => !columnIndexes.has(column),
  );
  if (missingColumns.length > 0) {
    throw new ShipmentCsvError(
      `必須列がありません: ${missingColumns.join(", ")}`,
      1,
    );
  }

  const rows = records.slice(1).flatMap((record): ShipmentImportRow[] => {
    if (record.cells.every((cell) => cell.trim().length === 0)) return [];

    const targetKind = cellAt(record, columnIndexes, "target_kind").trim();
    if (targetKind !== "cycle" && targetKind !== "one_time_order") {
      throw new ShipmentCsvError(
        "target_kind は cycle または one_time_order である必要があります。",
        record.rowNumber,
      );
    }

    return [
      {
        target_kind: targetKind,
        target_id: cellAt(record, columnIndexes, "target_id").trim(),
        tracking_number: cellAt(
          record,
          columnIndexes,
          "tracking_number",
        ).trim(),
      },
    ];
  });

  if (rows.length === 0) {
    throw new ShipmentCsvError("取込対象のデータ行がありません。");
  }

  return { rows, rowCount: rows.length };
}

function cellAt(
  record: CsvRecord,
  indexes: ReadonlyMap<string, number>,
  column: (typeof requiredColumns)[number],
): string {
  const index = indexes.get(column);
  return index === undefined ? "" : (record.cells[index] ?? "");
}

function parseCsvRecords(source: string): CsvRecord[] {
  const records: CsvRecord[] = [];
  let cells: string[] = [];
  let cell = "";
  let rowNumber = 1;
  let recordRowNumber = 1;
  let quoted = false;

  function finishRecord() {
    cells.push(cell);
    records.push({ cells, rowNumber: recordRowNumber });
    cells = [];
    cell = "";
  }

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];

    if (quoted) {
      if (character === '"') {
        if (source[index + 1] === '"') {
          cell += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += character;
        if (character === "\n") rowNumber += 1;
      }
      continue;
    }

    if (character === '"' && cell.length === 0) {
      quoted = true;
      continue;
    }
    if (character === ",") {
      cells.push(cell);
      cell = "";
      continue;
    }
    if (character === "\r" || character === "\n") {
      finishRecord();
      if (character === "\r" && source[index + 1] === "\n") index += 1;
      rowNumber += 1;
      recordRowNumber = rowNumber;
      continue;
    }
    cell += character;
  }

  if (quoted) {
    throw new ShipmentCsvError("引用符が閉じられていません。", recordRowNumber);
  }
  if (cell.length > 0 || cells.length > 0) finishRecord();

  return records;
}
